import httpService from "../../services/httpService";
import { ApiEndpoints } from "../../constants/apiEndpoints";

export interface Note {
  i: number;
  sM: number;
  eM: number;
  led: number[];
}

// Level enum for frontend use only (not used in API)
export enum BeatLevel {
  beginner = "beginner",
  intermediate = "intermediate",
  advanced = "advanced",
  pro = "pro",
  master = "master",
}

// API dokümantasyonuna uygun Beat interface
export interface Beat {
  beat_id: string;
  title: string;
  rhythm: string;
  bpm: number;
  duration_seconds: number;
  genre: string; // API'de genre var
  file_url: string;
  notes: string; // API'de string olarak
  created_at: string;
  updated_at: string;
}

// API dokümantasyonuna tam uyumlu Create Request
export interface CreateBeatRequest {
  title: string; // Required
  rhythm: string; // Required
  bpm: number; // Required
  duration_seconds: number; // Required
  genre: string; // Required
  file_url: string; // Required
  notes?: string; // Optional
}

// API dokümantasyonuna uygun Update Request
export interface UpdateBeatRequest {
  title?: string;
  rhythm?: string;
  bpm?: number;
  duration_seconds?: number;
  genre?: string;
  file_url?: string;
  notes?: string;
}

// API dokümantasyonuna göre List Response
interface BeatListResponse {
  data: Beat[];
  total: number;
  limit: number;
  offset: number;
}

// API'ye göre düzeltilmiş fetchBeats
export const fetchBeats = async (
  params: {
    limit?: number;
    offset?: number;
  } = {}
): Promise<Beat[]> => {
  try {
    const queryParams = new URLSearchParams({
      limit: (params.limit || 20).toString(),
      offset: (params.offset || 0).toString(),
    });

    const response = await httpService.get<BeatListResponse>(
      `${ApiEndpoints.BEATS}?${queryParams}`
    );

    console.log("🎵 Beats API Response:", response);

    // API BeatListResponse formatında döndürüyor
    if (
      response.data &&
      response.data.data &&
      Array.isArray(response.data.data)
    ) {
      console.log("✅ Beats data found:", response.data.data);
      return response.data.data;
    }

    // Fallback: Direkt array dönerse
    if (Array.isArray(response.data)) {
      console.log("✅ Beats data found (direct array):", response.data);
      return response.data;
    }

    console.log("⚠️ No beats found or invalid format");
    return [];
  } catch (error) {
    console.error("❌ Failed to fetch beats:", error);
    return [];
  }
};

// API'ye göre düzeltilmiş fetchBeat
export const fetchBeat = async (id: string): Promise<Beat | null> => {
  try {
    const response = await httpService.get<Beat>(
      `${ApiEndpoints.BEATS}/${id}`
    );
    console.log("🎵 Single Beat API Response:", response);

    // API direkt beat objesi döndürüyor
    if (
      response.data &&
      typeof response.data === "object" &&
      response.data.beat_id
    ) {
      console.log("✅ Beat data found:", response.data);
      return response.data;
    }

    console.warn("Beat data is undefined or invalid. Full response:", response);
    return null;
  } catch (error) {
    console.error("❌ Failed to fetch beat:", error);
    return null;
  }
};

// API dokümantasyonuna tam uyumlu addBeat
export const addBeat = async (data: CreateBeatRequest): Promise<Beat> => {
  try {
    const requestData = {
      title: data.title,
      rhythm: data.rhythm,
      bpm: data.bpm,
      duration_seconds: data.duration_seconds,
      genre: data.genre,
      file_url: data.file_url,
      notes: data.notes || "",
    };

    console.log("🎵 Creating beat with data:", requestData);

    const response = await httpService.post<Beat>(
      `${ApiEndpoints.BEATS}`,
      requestData
    );

    if (response.data && typeof response.data === "object") {
      return response.data;
    }

    throw new Error("Invalid response from server");
  } catch (error) {
    console.error("❌ Failed to create beat:", error);
    throw error;
  }
};

// API dokümantasyonuna tam uyumlu updateBeat
export const updateBeat = async (
  id: string,
  data: UpdateBeatRequest
): Promise<Beat> => {
  try {
    const requestData = {
      ...(data.title && { title: data.title }),
      ...(data.rhythm && { rhythm: data.rhythm }),
      ...(data.bpm && { bpm: data.bpm }),
      ...(data.duration_seconds && { duration_seconds: data.duration_seconds }),
      ...(data.genre && { genre: data.genre }),
      ...(data.file_url && { file_url: data.file_url }),
      ...(data.notes && { notes: data.notes }),
    };

    console.log("🎵 Updating beat with data:", requestData);

    const response = await httpService.put<Beat>(
      `${ApiEndpoints.BEATS}/${id}`,
      requestData
    );

    if (response.data && typeof response.data === "object") {
      return response.data;
    }

    throw new Error("Invalid response from server");
  } catch (error) {
    console.error("❌ Failed to update beat:", error);
    throw error;
  }
};

// API dokümantasyonuna göre deleteBeat
export const deleteBeat = async (id: string): Promise<void> => {
  try {
    await httpService.delete(`${ApiEndpoints.BEATS}/${id}`);
    console.log("🎵 Beat deleted successfully");
  } catch (error) {
    console.error("❌ Failed to delete beat:", error);
    throw error;
  }
};
